// ============================================================================
// PhonePreview — la publication telle qu'elle paraitra sur un telephone
// ============================================================================
// Composition LOCALE (UI_STANDARDS « composition locale legitime ») : un cadre
// de telephone en tokens slate-*, une barre d'etat en icones Lucide, l'en-tete
// « Actualite » du fil, puis le VRAI PublicationCard de @gs/ui avec les props du
// fil (feedCardProps) — jamais une copie. @gs/ui n'a aucun cadre de telephone :
// sa promotion est une question fermee du rapport, pas un ajout au paquet.
// Largeur d'ecran 360 px (w-90) : la largeur de reference de l'estimation de
// coupe (apercu.ts). La publication suivante du fil s'affiche attenuee sous la
// nouvelle, pour montrer ou elle se range. Les pieces jointes (maquette) se
// rendent dans la carte par PhoneAttachments.

import { PublicationCard } from '@gs/ui';
import type { TFunction } from 'i18next';
import { BatteryFull, Signal, Wifi } from 'lucide-react';
import type { Publication } from '../../hooks/useAccueil';
import { PhoneAttachments } from './PhoneAttachments';
import { feedCardProps, type FeedCardSource } from './feedCardProps';
import type { Attachment } from './piecesJointes';

export function PhonePreview({
  draft,
  imageObjectUrl,
  attachments = [],
  nextInFeed,
  locale,
  t,
  now = new Date(),
}: {
  /** Le brouillon, deja sous forme de publication (compteurs a zero). */
  draft: FeedCardSource;
  imageObjectUrl?: string | null;
  /** Pieces jointes du brouillon (maquette), rendues sous le pied de la carte. */
  attachments?: Attachment[];
  /** La publication qui la suivra dans le fil (contexte attenue), si connue. */
  nextInFeed?: Publication;
  locale: string;
  t: TFunction;
  now?: Date;
}) {
  const clock = new Intl.DateTimeFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);

  return (
    <figure
      aria-label={t('gest:accueil.editor.preview.aria')}
      className="mx-auto w-90 max-w-full"
    >
      <div className="rounded-4xl bg-slate-800 p-2.5 shadow-lg ring-1 ring-slate-900/10 dark:bg-slate-950 dark:ring-slate-700">
        <div className="overflow-hidden rounded-3xl bg-slate-100 dark:bg-slate-900">
          {/* Barre d'etat — decor, cachee aux lecteurs d'ecran. */}
          <div
            aria-hidden
            className="flex items-center justify-between px-5 pb-1 pt-2.5 text-meta font-semibold text-slate-800 dark:text-slate-100"
          >
            <span className="tabular-nums">{clock}</span>
            <span className="h-4 w-16 rounded-full bg-slate-800 dark:bg-slate-950" />
            <span className="flex items-center gap-1">
              <Signal size={13} />
              <Wifi size={13} />
              <BatteryFull size={15} />
            </span>
          </div>

          <div className="border-b border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800">
            <p className="text-head font-semibold text-slate-800 dark:text-slate-100">
              {t('gest:accueil.feed.title')}
            </p>
          </div>

          <div
            data-testid="phone-screen"
            className="h-150 space-y-3 overflow-y-auto px-3 py-3"
          >
            <PublicationCard {...feedCardProps(draft, locale, t, now)} imageObjectUrl={imageObjectUrl}>
              {attachments.length > 0 ? <PhoneAttachments attachments={attachments} locale={locale} t={t} /> : null}
            </PublicationCard>
            {nextInFeed && (
              <div aria-hidden className="pointer-events-none opacity-50">
                <PublicationCard {...feedCardProps(nextInFeed, locale, t, now)} />
              </div>
            )}
          </div>

          <div aria-hidden className="flex justify-center bg-slate-100 py-2 dark:bg-slate-900">
            <span className="h-1 w-28 rounded-full bg-slate-300 dark:bg-slate-600" />
          </div>
        </div>
      </div>
      <figcaption className="mt-3 text-center text-meta text-slate-500 dark:text-slate-400">
        {t('gest:accueil.editor.preview.caption')}
      </figcaption>
    </figure>
  );
}
